import get from 'lodash/get';
import { getRadiuses } from '../../../utils/radiusSearch';
import {
  getPortalDefaultCountry,
  portalHasCountryFilter
} from '../../../../../utils/locationHelper';
import {
  getCities,
  getRegions,
  getSubdivisions
} from '../../../../../utils/multiFacetHelper';
import sortBy from 'lodash/sortBy';

const shouldCollapsibleBeOpen = (facetsConfig, facetName) => {
  const facetConfig = facetsConfig.filter(
    (facet) => facet.name === facetName
  )[0];
  return get(facetConfig, 'initialState.open', false);
};

const getLocationFilterData = (filtersData, facets, context) => {
  const radiuses = getRadiuses();
  const selectedCountry = portalHasCountryFilter(context)
    ? filtersData.country
    : getPortalDefaultCountry();
  const selectedRadius = radiuses.find((r) => r.value === filtersData.radius);
  const locationFilterData = {
    radiusesData: {
      radiuses: radiuses,
      selected: selectedRadius
    },
    countriesData: {
      countries: get(facets, 'country', []),
      selected: selectedCountry
    },
    citiesData: {
      cities: getCities(facets, selectedCountry),
      selected: filtersData.city
    },
    regionsData: {
      regions: sortBy(getRegions(facets, selectedCountry), 'value'),
      selected: filtersData.region
    },
    subdivisionsData: {
      subdivisions: getSubdivisions(facets, selectedCountry),
      selected: filtersData.subdivision
    }
  };

  return locationFilterData;
};

const getLocationFilterConfig = (facetConfig, tracking) => {
  const locationFilterConfig = {};
  locationFilterConfig.shouldCollapsibleBeOpen = {
    radius: shouldCollapsibleBeOpen(facetConfig, 'radius') ? 'open' : 'closed',
    country: shouldCollapsibleBeOpen(facetConfig, 'country')
      ? 'open'
      : 'closed',
    city: shouldCollapsibleBeOpen(facetConfig, 'city') ? 'open' : 'closed',
    region: shouldCollapsibleBeOpen(facetConfig, 'region') ? 'open' : 'closed',
    subdivision: shouldCollapsibleBeOpen(facetConfig, 'subdivision')
      ? 'open'
      : 'closed'
  };
  locationFilterConfig.tracking = tracking;

  return locationFilterConfig;
};

const shouldSeeSearchAlert = (context, position, currentLanguage) => {
  const supportsSearchAlerts = context.supports?.searchAlerts;
  const isDisableSearchAlerts =
    !!context.languages?.[currentLanguage]?.searchAlerts;
  const searchAlerts =
    position === 'desktop' && supportsSearchAlerts && !isDisableSearchAlerts;
  return searchAlerts;
};

const prepareFilters = (type, value, wasRegionChanged) => {
  let wasRegionChangedByUser = wasRegionChanged;
  let location = {};
  let newStateProperty = {};

  const clearLocationSpecificFields = () => {
    location.country = '';
    location.region = '';
    location.subdivision = '';
    location.city = [];
    location.radius = '0';
    location.latlon = '';
    location.placeId = '';
  };

  if (type === 'location_place_id') {
    newStateProperty = { placeId: value.placeId };
    clearLocationSpecificFields();
    delete location.placeId;
    if (value.placeId && value.placeId !== '') {
      location.radius = value.radius;
    }
  } else if (type === 'coordinates') {
    newStateProperty = { latlon: value.latlon };
    clearLocationSpecificFields();
    delete location.latlon;
    if (value.latlon && value.latlon !== '') {
      location.radius = value.radius;
    }
  } else if (type === 'location_clear') {
    newStateProperty = { latlon: '', placeId: '' };
    clearLocationSpecificFields();
    location.radius = value.radius || '0';
  } else if (type === 'location_clear_latlon') {
    newStateProperty = { latlon: '' };
    location.radius = value.radius;
  } else if (type === 'region') {
    newStateProperty = { region: value };
    clearLocationSpecificFields();
    location.country = undefined;
    location.region = undefined;
    location.radius = undefined;
    wasRegionChangedByUser = value !== 'all';
  } else if (type === 'subdivision') {
    newStateProperty = { subdivision: value };
    clearLocationSpecificFields();
    location.subdivision = undefined;
    location.country = undefined;
    location.radius = undefined;
  } else if (type === 'country') {
    newStateProperty = { country: value };
    clearLocationSpecificFields();
    location.country = undefined;
  } else if (type === 'radius') {
    newStateProperty = { radius: value };
    location.radius = undefined;
  } else {
    newStateProperty = { [type]: value };
    if ((type === 'placeId' && !value) ||
        (type === 'latlon' && !value)) {
      newStateProperty.radius = '0';
    }
  }

  Object.keys(location).forEach(key => location[key] === undefined && delete location[key]);

  return {location, newStateProperty, wasRegionChangedByUser};
};

const updateExclusiveFilters = (prevState, type) => {
  let updatedData = { ...prevState.data };
  let stateChanged = false;

  const clearSpecificLocation = () => {
    if (updatedData.country || updatedData.region || updatedData.subdivision || (updatedData.city && updatedData.city.length > 0)) {
        updatedData.country = '';
        updatedData.city = [];
        updatedData.region = '';
        updatedData.subdivision = '';
        stateChanged = true;
    }
  };

  const clearLatLon = () => { if (updatedData.latlon) { updatedData.latlon = ''; stateChanged = true; } };
  const clearPlaceId = () => { if (updatedData.placeId) { updatedData.placeId = ''; stateChanged = true; } };
  const clearRadius = () => { if (updatedData.radius !== '0') {updatedData.radius = '0'; stateChanged = true; }};

  if (type === 'location_place_id') {
    clearSpecificLocation();
    clearLatLon();
  } else if (type === 'coordinates') {
    clearSpecificLocation();
    clearPlaceId();
  }

  const countryBlockLocations = ['country', 'region', 'subdivision', 'city'];
  if (countryBlockLocations.includes(type)) {
    clearLatLon();
    clearPlaceId();
    clearRadius();
  }

  if (type === 'radius' && !prevState.data.latlon && !prevState.data.placeId) {
      clearSpecificLocation();
  }

  return stateChanged ? { ...prevState, data: updatedData } : prevState;
};

const updateNewFiltersState = (prevState, type, value, doDebounce) => {
  const stateWithExclusions = updateExclusiveFilters(prevState, type);
  const {location, newStateProperty, wasRegionChangedByUser} = prepareFilters(type, value, stateWithExclusions.wasRegionChangedByUser);


  const newState = { ...stateWithExclusions };
  newState.data = {
    ...stateWithExclusions.data,
    ...newStateProperty,
    ...location
  };
  newState.wasRegionChangedByUser = wasRegionChangedByUser;
  newState.doDebounce = doDebounce;
  return newState;
};

export {
  updateExclusiveFilters, getLocationFilterData, getLocationFilterConfig, shouldCollapsibleBeOpen, shouldSeeSearchAlert, prepareFilters, updateNewFiltersState,
};

